/**
 * 五子棋AI模块
 * 实现Minimax算法和Alpha-Beta剪枝优化
 */
import { PositionEvaluator } from './evaluator.js';
import { PatternRecognizer } from './patterns.js';
import { SimpleVCFVCTSolver } from './vcf_vct_simple.js';

export class GomokuAI {
    constructor(depth = 4, maxTime = 3000) {
        this.maxDepth = depth;
        this.maxTime = maxTime; // 最大思考时间（毫秒）
        this.evaluator = new PositionEvaluator();
        this.patternRecognizer = new PatternRecognizer();
        this.vcfVctSolver = new SimpleVCFVCTSolver(this.patternRecognizer);
        this.nodesSearched = 0;
        this.startTime = 0;
        this.transpositionTable = new Map(); // 简单的置换表
    }

    /**
     * 获取最佳走法
     * @param {Board} board 棋盘
     * @returns {Object|null} 最佳走法
     */
    getBestMove(board) {
        if (board.gameOver) {
            return null;
        }

        this.startTime = Date.now();
        this.nodesSearched = 0;
        this.transpositionTable.clear();

        // 首先检查必胜/必败
        const criticalMove = this.findCriticalMove(board);
        if (criticalMove) {
            return criticalMove;
        }

        // 尝试VCF/VCT算杀
        const killResult = this.tryVCFVCT(board);
        if (killResult.success) {
            console.log(`${killResult.type}算杀成功，深度: ${killResult.depth}`);
            return killResult.moves[0];
        }

        // 迭代加深搜索
        let bestMove = null;
        let bestScore = -Infinity;

        for (let depth = 1; depth <= this.maxDepth; depth++) {
            if (Date.now() - this.startTime > this.maxTime) {
                break;
            }

            const { move, score } = this.iterativeDeepening(board, depth);
            if (move) {
                bestMove = move;
                bestScore = score;
            }

            // 如果找到必胜走法，立即返回
            if (Math.abs(bestScore) >= 100000) {
                break;
            }
        }

        return bestMove;
    }

    /**
     * 尝试VCF/VCT算杀
     * @param {Board} board 棋盘
     * @returns {Object} 算杀结果
     */
    tryVCFVCT(board) {
        const player = board.currentPlayer;

        // 限制算杀时间，避免思考过长
        const startTime = Date.now();
        const maxKillTime = this.maxTime * 0.3; // 30%时间用于算杀

        const result = this.vcfVctSolver.solve(board, player);

        // 如果算杀成功且时间允许，使用算杀结果
        if (result.success && (Date.now() - startTime) < maxKillTime) {
            return {
                success: true,
                moves: result.moves,
                type: result.type,
                depth: result.depth
            };
        }

        return { success: false, moves: [], type: 'none' };
    }

    /**
     * 查找关键走法（必胜或必须防守）
     * @param {Board} board 棋盘
     * @returns {Object|null} 关键走法
     */
    findCriticalMove(board) {
        const candidates = this.evaluator.getCandidateMoves(board, 10);

        // 检查是否有必胜走法
        for (const move of candidates) {
            const testBoard = board.clone();
            testBoard.makeMove(move.row, move.col);
            if (testBoard.gameOver && testBoard.winner === board.currentPlayer) {
                return move;
            }
        }

        // 检查是否必须防守（对手有必胜走法）
        const opponent = 3 - board.currentPlayer;
        const testBoard = board.clone();
        testBoard.currentPlayer = opponent;

        const opponentCandidates = this.evaluator.getCandidateMoves(testBoard, 10);
        for (const move of opponentCandidates) {
            const checkBoard = testBoard.clone();
            checkBoard.makeMove(move.row, move.col);
            if (checkBoard.gameOver && checkBoard.winner === opponent) {
                return move; // 防守对手的必胜位置
            }
        }

        return null;
    }

    /**
     * 迭代加深搜索
     * @param {Board} board 棋盘
     * @param {number} depth 深度
     * @returns {Object} 最佳走法和分数
     */
    iterativeDeepening(board, depth) {
        const candidates = this.evaluator.getCandidateMoves(board);
        let bestMove = null;
        let bestScore = -Infinity;

        for (const move of candidates) {
            const testBoard = board.clone();
            testBoard.makeMove(move.row, move.col);

            const score = this.alphaBeta(testBoard, depth - 1, -Infinity, Infinity, false);

            if (score > bestScore) {
                bestScore = score;
                bestMove = move;
            }

            // 如果超时，返回当前最佳
            if (Date.now() - this.startTime > this.maxTime) {
                break;
            }
        }

        return { move: bestMove, score: bestScore };
    }

    /**
     * Alpha-Beta剪枝搜索
     * @param {Board} board 棋盘
     * @param {number} depth 深度
     * @param {number} alpha Alpha值
     * @param {number} beta Beta值
     * @param {boolean} maximizing 是否为最大化玩家
     * @returns {number} 评估分数
     */
    alphaBeta(board, depth, alpha, beta, maximizing) {
        // 检查时间限制
        if (Date.now() - this.startTime > this.maxTime) {
            return this.evaluator.evaluate(board);
        }

        // 终止条件
        if (depth === 0 || board.gameOver) {
            return this.evaluator.evaluate(board);
        }

        // 置换表查询
        const boardKey = this.getBoardKey(board);
        if (this.transpositionTable.has(boardKey)) {
            const entry = this.transpositionTable.get(boardKey);
            if (entry.depth >= depth) {
                if (entry.flag === 'exact') {
                    return entry.score;
                } else if (entry.flag === 'alpha' && entry.score <= alpha) {
                    return entry.score;
                } else if (entry.flag === 'beta' && entry.score >= beta) {
                    return entry.score;
                }
            }
        }

        this.nodesSearched++;
        const candidates = this.evaluator.getCandidateMoves(board,
            depth > 2 ? Math.min(15, 8 + depth) : 25);

        let bestMove = null;
        let score;

        if (maximizing) {
            score = -Infinity;

            for (const move of candidates) {
                const testBoard = board.clone();
                testBoard.makeMove(move.row, move.col);
                const moveScore = this.alphaBeta(testBoard, depth - 1, alpha, beta, false);

                if (moveScore > score) {
                    score = moveScore;
                    bestMove = move;
                }

                alpha = Math.max(alpha, score);
                if (beta <= alpha) {
                    break; // Alpha剪枝
                }
            }
        } else {
            score = Infinity;

            for (const move of candidates) {
                const testBoard = board.clone();
                testBoard.makeMove(move.row, move.col);
                const moveScore = this.alphaBeta(testBoard, depth - 1, alpha, beta, true);

                if (moveScore < score) {
                    score = moveScore;
                    bestMove = move;
                }

                beta = Math.min(beta, score);
                if (beta <= alpha) {
                    break; // Beta剪枝
                }
            }
        }

        // 存储到置换表
        let flag = 'exact';
        if (score <= alpha) {
            flag = 'alpha';
        } else if (score >= beta) {
            flag = 'beta';
        }

        this.transpositionTable.set(boardKey, {
            score,
            depth,
            flag,
            move: bestMove
        });

        return score;
    }

    /**
     * 生成棋盘的哈希键
     * @param {Board} board 棋盘
     * @returns {string} 哈希键
     */
    getBoardKey(board) {
        let key = `${board.currentPlayer},`;
        for (let i = 0; i < board.size; i++) {
            for (let j = 0; j < board.size; j++) {
                if (board.board[i][j] !== 0) {
                    key += `${i},${j},${board.board[i][j]};`;
                }
            }
        }
        return key;
    }

    /**
     * 获取AI统计信息
     * @returns {Object} 统计信息
     */
    getStats() {
        return {
            nodesSearched: this.nodesSearched,
            timeElapsed: Date.now() - this.startTime,
            nodesPerSecond: Math.round(this.nodesSearched / Math.max(1, (Date.now() - this.startTime) / 1000)),
            transpositionTableSize: this.transpositionTable.size
        };
    }

    /**
     * 设置难度
     * @param {number} level 难度级别 (1-5)
     */
    setDifficulty(level) {
        const settings = {
            1: { depth: 2, time: 1000 },  // 初级
            2: { depth: 3, time: 2000 },  // 中级
            3: { depth: 4, time: 3000 },  // 高级
            4: { depth: 5, time: 4000 },  // 专家
            5: { depth: 6, time: 5000 }   // 大师
        };

        const config = settings[level] || settings[3];
        this.maxDepth = config.depth;
        this.maxTime = config.time;
    }
}